import React, { useState, useRef, useEffect } from "react";
import { Search } from "lucide-react";
import { useTranslation } from "react-i18next";

function SearchBar({ onSelect }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  const abortRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      if (timerRef.current) clearTimeout(timerRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const search = (q) => {
    setQuery(q);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (q.length < 2) {
      if (abortRef.current) abortRef.current.abort();
      setResults([]);
      setOpen(false);
      return;
    }

    timerRef.current = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=5`,
          { headers: { "Accept-Language": "en" }, signal: controller.signal }
        );
        const data = await res.json();
        if (controller.signal.aborted) return;
        setResults(
          data.map((r) => ({
            name: r.display_name,
            lat: parseFloat(r.lat),
            lng: parseFloat(r.lon),
          }))
        );
        setOpen(true);
      } catch {
        if (!controller.signal.aborted) setResults([]);
      }
    }, 400);
  };

  const handleSelect = (result) => {
    onSelect([result.lat, result.lng]);
    setQuery(result.name.split(",")[0]);
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative flex-1 min-w-0 max-w-xs">
      <div className="relative">
        <Search
          size={14}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-th-muted pointer-events-none"
        />
        <input
          type="text"
          value={query}
          onChange={(e) => search(e.target.value)}
          placeholder={t("map.search")}
          className="w-full pl-8 pr-3 py-2 text-base rounded-full bg-th-overlay backdrop-blur-md text-th-text placeholder-th-muted shadow-lg border border-th-border outline-none focus:border-th-accent-border focus:ring-1 focus:ring-th-accent-border transition-all"
        />
      </div>
      {open && results.length > 0 && (
        <ul className="mt-1.5 bg-th-card backdrop-blur-md rounded-xl shadow-xl border border-th-border overflow-hidden max-h-48 overflow-y-auto">
          {results.map((r, i) => (
            <li key={i}>
              <button
                type="button"
                onClick={() => handleSelect(r)}
                className="w-full text-left px-3 py-2.5 text-sm text-th-text-sec hover:bg-th-surface active:bg-th-surface truncate transition-colors border-b border-th-border last:border-0"
              >
                {r.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default SearchBar;
